import React, { useState } from 'react';
import Navbar from "../../component/HeaderData";
import Footer from "../../component/FooterData";
import BackButton from '../../component/Common/BackBtn';
import { useHistory } from 'react-router-dom';
import PageSubTitle from '../../component/Common/PageSubTitle';
import { useLocation } from 'react-router-dom';
import { Bar } from 'react-chartjs-2';
import { Line } from 'react-chartjs-2';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    Title,
    Tooltip,
    Legend,
} from 'chart.js';
import { ChartOptions } from 'chart.js';

const IntakeDateQualityDetails = () => {
    const [isLoading, setIsLoading] = useState(false);
    const [activeTab, setActiveTab] = useState<'Definition' | 'Data'>('Definition'); // State to toggle between Definition and Data
    const history = useHistory();
    const location = useLocation();
    const [selectedYear, setSelectedYear] = useState('All');
    const [selectedQuarter, setSelectedQuarter] = useState('All');
    const [selectedReport, setSelectedReport] = useState('All');
    ChartJS.register(CategoryScale, LinearScale, Title, Tooltip, Legend);

    interface LocationState {
        recordData?: {
            measure_id: keyof typeof chartData;
        };
    }

    const { recordData } = (location.state as LocationState) || {};

    const chartData = {
        Timeliness: {
            labels: ['On Time', 'Late'],
            datasets: [
                {
                    data: [80, 20],
                    backgroundColor: ['#4caf50', '#f44336'], // Green and Red
                    borderWidth: 1,
                },
            ],
        },
        QualityCompliance: {
            labels: ['Compliant', 'Non-Compliant'],
            datasets: [
                {
                    data: [70, 30],
                    backgroundColor: ['#2196f3', '#ff9800'], // Blue and Orange
                    borderWidth: 1,
                },
            ],
        },
    };

    const definitions = {
        Timeliness: 'This measure is recommended to  determine the plans punctuality in submission of the reports that they are required to submit either based on standard frequency as semi-monthly (bi-weekly), monthly, quarterly, semi-annually and annually or they are asked to submit on ad hoc basis. This measure is inferred based on the comparison within the actual submission date, extension date and due date. <br>Here are the metrices used in scoring fora this measure:</br>',
        QualityCompliance: 'This measure refers to the accuracy, completeness and consistency of the reports submitted by the plans. This measure is inferred based on the number of submissions, number of validation errors, number of reviews and the reopen request submitted per the report instance. <br>Here are the metrices used in scoring for this measure:</br>',
    };

    // Bar chart data for Timeliness
    const timelinessBarChartData = {
        labels: ['1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11', '12'],
        datasets: [
            {
                label: 'UHC',
                data: [2.0, 5.0, 4.0, 6.0, 10.0, 10.0, 10.0, 10.0, 1.0, 10.0, 9.0, 8.0],
                backgroundColor: '#4caf50', // Green
            },
            {
                label: 'NTC',
                data: [8.0, 9.0, 8.5, 4.0, 3.0, 2.0, 1.0, 8.0, 8.0, 6.0, 5.0, 10.0],
                backgroundColor: '#2196f3', // Blue
            },
            {
                label: 'MHN',
                data: [4.0, 3.0, 2.0, 10.0, 9.0, 8.0, 7.0, 6.0, 5.0, 4.0, 3.0, 2.0],
                backgroundColor: '#ff9800', // Orange
            },
        ],
    };

    const qualityComplianceLineChartData = {
        labels: ['1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11', '12'], // Row Labels
        datasets: [
            {
                label: 'MHN',
                data: [5.34, 4.23, 3.11, 6.72, 6.06, 8.28, 7.17, 6.61, 6.06, 4.94, 3.83, 2.72],
                borderColor: '#ff9800', // Orange
                backgroundColor: 'rgba(255, 152, 0, 0.2)', // Light orange fill
                tension: 0.4, // Smooth curve
            },
            {
                label: 'NTC',
                data: [5.37, 9.34, 8.09, 4.94, 3.83, 2.72, 2.44, 7.50, 6.06, 6.28, 6.39, 7.89],
                borderColor: '#2196f3', // Blue
                backgroundColor: 'rgba(33, 150, 243, 0.2)', // Light blue fill
                tension: 0.4, // Smooth curve
            },
            {
                label: 'UHC',
                data: [6.63, 6.23, 4.06, 9.78, 10.00, 10.00, 10.00, 7.89, 1.83, 6.72, 6.06, 8.28],
                borderColor: '#4caf50', // Green
                backgroundColor: 'rgba(76, 175, 80, 0.2)', // Light green fill
                tension: 0.4, // Smooth curve
            }
        ],
    };

    const filteredBarChartData = {
        ...timelinessBarChartData,
        datasets: timelinessBarChartData.datasets.map((dataset) => ({
            ...dataset,
            data: dataset.data.filter((_, index) => {
                // Year Filter
                if (selectedYear === '2025' && index >= 6) return false; // Show only first 6 months for 2025

                // Quarter Filter (applies regardless of the year)
                if (selectedQuarter === 'Q1' && index >= 3) return false; // Show only Q1 (first 3 months)
                if (selectedQuarter === 'Q2' && (index < 3 || index >= 6)) return false; // Show only Q2 (months 4-6)
                if (selectedQuarter === 'Q3' && (index < 6 || index >= 9)) return false; // Show only Q3 (months 7-9)
                if (selectedQuarter === 'Q4' && index < 9) return false; // Show only Q4 (months 10-12)

                // Default: Show all data for 2024 if no quarter is selected
                if (selectedYear === '2024') return true;

                // Report Filter
                if (selectedReport === 'Appeals Log') return true; // Show all data for "Appeals Log"
                if (selectedReport === 'Other Report' && dataset.label === 'UHC') return false; // Example: Hide UHC for "Other Report"

                // Default: Show all data
                return true;
            }),
        })),
    };

    const filteredLineChartData = {
        ...qualityComplianceLineChartData,
        datasets: qualityComplianceLineChartData.datasets.map((dataset) => ({
            ...dataset,
            data: dataset.data.filter((_, index) => {
                // Year Filter
                if (selectedYear === '2025' && index >= 6) return false; // Show only first 6 months for 2025

                // Quarter Filter (applies regardless of the year)
                if (selectedQuarter === 'Q1' && index >= 3) return false; // Show only Q1 (first 3 months)
                if (selectedQuarter === 'Q2' && (index < 3 || index >= 6)) return false; // Show only Q2 (months 4-6)
                if (selectedQuarter === 'Q3' && (index < 6 || index >= 9)) return false; // Show only Q3 (months 7-9)
                if (selectedQuarter === 'Q4' && index < 9) return false; // Show only Q4 (months 10-12)

                // Default: Show all data for 2024 if no quarter is selected
                if (selectedYear === '2024') return true;

                // Report Filter
                if (selectedReport === 'Appeals Log') return true; // Show all data for "Appeals Log"
                if (selectedReport === 'Other Report' && dataset.label === 'UHC') return false; // Example: Hide UHC for "Other Report"

                // Default: Show all data
                return true;
            }),
        })),
    };

    const barChartOptions = {
        responsive: true,
        plugins: {
            legend: {
                position: 'top' as const,
                display: true,
            }
        },
        scales: {
            x: {
                title: {
                    display: true,
                    text: 'Months', // Title for the X-axis
                },
            },
            y: {
                title: {
                    display: true,
                    text: 'Average of Score', // Title for the Y-axis
                },
                beginAtZero: true, // Ensure the Y-axis starts at 0
            },
        },
    };

    const lineChartOptions = {
        responsive: true,
        plugins: {
            legend: {
                position: 'top' as const,
                display: true,
            },
            title: {
                display: true,
                text: 'Quality Compliance Line Chart',
            },
        },
        scales: {
            x: {
                title: {
                    display: true,
                    text: 'Months', // Title for the X-axis
                },
            },
            y: {
                title: {
                    display: true,
                    text: 'Average of Score', // Title for the Y-axis
                },
                beginAtZero: true, // Ensure the Y-axis starts at 0
            },
        },
    };

    interface TableProps {
        title: string;
        headers: string[];
        rows: Array<string[]>;
    }

    const Table: React.FC<TableProps> = ({ title, headers, rows }) => {
        return (
            <div style={{ width: 'auto', marginBottom: '20px' }}>
                <h3 style={{ textAlign: 'center', marginBottom: '20px', fontSize: '18px' }}>{title}</h3>
                <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '14px' }}>
                    <thead>
                        <tr>
                            {headers.map((header, index) => (
                                <th
                                    key={index}
                                    style={{
                                        border: '1px solid #ccc',
                                        padding: '8px',
                                        backgroundColor: '#f0f8ff',
                                        width: `${100 / headers.length}%`,
                                    }}
                                >
                                    {header}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((row, rowIndex) => (
                            <tr key={rowIndex}>
                                {row.map((cell, cellIndex) => (
                                    <td
                                        key={cellIndex}
                                        style={{
                                            border: '1px solid #ccc',
                                            padding: '8px',
                                        }}
                                    >
                                        {cell}
                                    </td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        );
    };

    const plans = [
        { name: 'UHC', data: [2, 5, 4, 6, 10, 10, 10, 10, 1, 10, 9, 8], color: '#4caf50' },
        { name: 'NTC', data: [8, 9, 8.5, 4, 3, 2, 1, 8, 8, 6, 5, 10], color: '#2196f3' },
        { name: 'MHN', data: [4, 3, 2, 10, 9, 8, 7, 6, 5, 4, 3, 2], color: '#ff9800' },
    ];

    const trendlineChartData = {
        labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'], // Months
        datasets: [
            ...plans.map(plan => ({
                label: plan.name, // Plan's name as the label
                data: plan.data, // Plan's data (month-wise)
                borderColor: plan.color, // Line color for each plan
                borderWidth: 2,
                fill: false, // Don't fill under the line, just show the line
            })),
            {
                label: 'Baseline (8)', // Label for the baseline
                data: Array(12).fill(8), // Create an array with 12 values, all set to 8
                borderColor: '#f44336', // Red color for the baseline
                borderWidth: 2,
                borderDash: [5, 5], // Dashed line
                fill: false, // No fill under the line
            },
        ],
    };

    const trendlineChartOptions: ChartOptions<'line'> = {
        responsive: true,
        plugins: {
            legend: {
                position: 'top', // Position the legend at the top
                display: true, // Show the legend
            },
            // title: {
            //     display: true,
            //     text: 'Trendline Graph', // Title of the chart
            // },
        },
        scales: {
            x: {
                title: {
                    display: true,
                    text: 'Months', // X-axis title
                },
            },
            y: {
                title: {
                    display: true,
                    text: 'Values', // Y-axis title
                },
                beginAtZero: true, // Start the Y-axis from 0
            },
        },
    };

    return (
        <>
            <Navbar />
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                <div style={{ alignSelf: 'flex-start' }}>
                    <BackButton />
                </div>
                <div style={{ width: '100%', display: 'flex', justifyContent: 'center', marginTop: '20px' }}>
                    <PageSubTitle
                        text={
                            recordData?.measure_id === 'Timeliness'
                                ? 'Intake Process Score - Timeliness'
                                : recordData?.measure_id === 'QualityCompliance'
                                    ? 'Intake Process Score - Quality Compliance'
                                    : 'Intake Process Score'
                        }
                    />
                </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'center', marginTop: '20px', gap: '10px' }}>
                {/* Buttons to toggle between Definition and Data */}
                <button
                    onClick={() => setActiveTab('Definition')}
                    style={{
                        padding: '10px 20px',
                        backgroundColor: activeTab === 'Definition' ? '#007bff' : '#f9f9f9',
                        color: activeTab === 'Definition' ? '#fff' : '#000',
                        border: '1px solid #ccc',
                        borderRadius: '5px',
                        cursor: 'pointer',
                        fontWeight: activeTab === 'Definition' ? 'bold' : 'normal',
                    }}
                >
                    Definition
                </button>
                <button
                    onClick={() => setActiveTab('Data')}
                    style={{
                        padding: '10px 20px',
                        backgroundColor: activeTab === 'Data' ? '#007bff' : '#f9f9f9',
                        color: activeTab === 'Data' ? '#fff' : '#000',
                        border: '1px solid #ccc',
                        borderRadius: '5px',
                        cursor: 'pointer',
                        fontWeight: activeTab === 'Data' ? 'bold' : 'normal',
                    }}
                >
                    Data
                </button>
            </div>

            <div className="form-layout" style={{ marginTop: '20px', textAlign: 'center' }}>
                {activeTab === 'Definition' && recordData ? (
                    <div>
                        <p
                            dangerouslySetInnerHTML={{
                                __html: definitions[recordData.measure_id] || 'No definition available for this measure.',
                            }}
                        ></p>
                    </div>
                ) : null}

                {activeTab === 'Definition' && recordData?.measure_id === 'Timeliness' && recordData ? (
                    <div style={{ display: 'flex', justifyContent: 'center', gap: '40px', marginTop: '40px' }}>
                        {/* Submission Timeliness and Score Table */}
                        <Table
                            title="Submission Timeliness and Score"
                            headers={['Submission Timeliness', 'Score(10)']}
                            rows={[
                                ['By due date', '10'],
                                ['1 to 5', '8'],
                                ['6 to 10', '6'],
                                ['11 to 15', '4'],
                                ['16 or 30', '3'],
                                ['31 or 60', '2'],
                                ['61 or 90', '1'],
                                ['Submitted later than 90', '0'],
                                ['Due but not submitted', '0'],
                            ]}
                        />

                        {/* Extension Days and Score Table */}
                        <Table
                            title="Extension Days and Score"
                            headers={['Extension Days', 'Score(10)']}
                            rows={[
                                ['0', '10'],
                                ['1', '8'],
                                ['2', '7'],
                                ['3', '6'],
                                ['4 to 5', '4'],
                                ['6 to 10', '3'],
                                ['11 to 15', '2'],
                            ]}
                        />
                    </div>
                ) : null}

                {activeTab === 'Definition' && recordData?.measure_id === 'QualityCompliance' && recordData ? (
                    <div style={{ display: 'flex', justifyContent: 'center', gap: '40px', marginTop: '40px' }}>
                        {/* Submission Count and Score Table */}
                        <Table
                            title="Submission Count and Score"
                            headers={['Submission Count', 'Score(10)']}
                            rows={[
                                ['1', '10'],
                                ['2', '8'],
                                ['3', '6'],
                                ['4 to 5', '2'],
                                ['6 to 10', '1'],
                                ['Other', '0'],
                            ]}
                        />

                        {/* Reviews Count and Score Table */}
                        <Table
                            title="Reviews Count and Score"
                            headers={['Reviews Count', 'Score(10)']}
                            rows={[
                                ['1', '10'],
                                ['2', '8'],
                                ['3', '6'],
                                ['4 to 5', '2'],
                                ['6 to 10', '1'],
                                ['Other', '0'],
                            ]}
                        />

                        {/* Basic Validation Errors and Score Table */}
                        <Table
                            title="Basic Validation Errors and Score"
                            headers={['Basic Validation Errors', 'Score(10)']}
                            rows={[
                                ['0 to 5', '10'],
                                ['6 to 10', '8'],
                                ['11 to 15', '6'],
                                ['16 to 25', '2'],
                                ['26 to 50', '1'],
                                ['Other', '0'],
                            ]}
                        />

                        {/* Reopen Request Count and Score Table */}
                        <Table
                            title="Reopen Request Count and Score"
                            headers={['Reopen Request Count', 'Score(10)']}
                            rows={[
                                ['0', '10'],
                                ['1', '8'],
                                ['2', '4'],
                                ['3', '2'],
                                ['4 to 5', '1'],
                                ['Other', '0'],
                            ]}
                        />
                    </div>
                ) : null}

                {activeTab === 'Definition' && recordData ? (
                    <div style={{ marginTop: '20px', textAlign: 'center' }}>
                        <p style={{ fontSize: '14px', color: '#555' }}>
                            <strong>Note:</strong> These metrices are configurable within NEMCIS.
                        </p>
                    </div>
                ) : null}

                {activeTab === 'Data' ? (
                    <div>
                        {/* Dropdown filters */}
                        <div style={{ display: 'flex', justifyContent: 'center', gap: '10px', marginBottom: '20px' }}>
                            <select
                                value={selectedYear}
                                onChange={(e) => setSelectedYear(e.target.value)}
                                style={{
                                    padding: '8px',
                                    border: '1px solid #ccc',
                                    borderRadius: '5px',
                                    cursor: 'pointer',
                                }}
                            >
                                <option value="All">All Years</option>
                                <option value="2025">2025</option>
                                <option value="2024">2024</option>
                            </select>
                            <select
                                value={selectedQuarter}
                                onChange={(e) => setSelectedQuarter(e.target.value)}
                                style={{
                                    padding: '8px',
                                    border: '1px solid #ccc',
                                    borderRadius: '5px',
                                    cursor: 'pointer',
                                }}
                            >
                                <option value="All">All Quarters</option>
                                <option value="Q1">Q1</option>
                                <option value="Q2">Q2</option>
                                <option value="Q3">Q3</option>
                                <option value="Q4">Q4</option>
                            </select>
                            <select
                                value={selectedReport}
                                onChange={(e) => setSelectedReport(e.target.value)}
                                style={{
                                    padding: '8px',
                                    border: '1px solid #ccc',
                                    borderRadius: '5px',
                                    cursor: 'pointer',
                                }}
                            >
                                <option value="All">All</option>
                                <option value="Appeals Log">Appeals Log</option>
                                <option value="Other Report">Other Report</option>
                            </select>
                        </div>

                        {/* {activeTab === 'Data' && recordData?.measure_id === 'Timeliness' ? (
                            <div style={{ width: '80%', margin: '20px auto' }}>
                                <Bar data={filteredBarChartData} options={barChartOptions} />
                            </div>
                        ) : null} */}

                        {activeTab === 'Data' && recordData?.measure_id === 'QualityCompliance' ? (
                            <div style={{ width: '80%', margin: '20px auto' }}>
                                <Line data={filteredLineChartData} options={lineChartOptions} />
                            </div>
                        ) : null}

                        {activeTab === 'Data' ? (
                            <div style={{ width: '80%', margin: '20px auto' }}>
                                <Line data={trendlineChartData} options={trendlineChartOptions} />
                            </div>
                        ) : null}
                    </div>
                ) : null}
            </div >

            <Footer />
        </>
    );
};

export default IntakeDateQualityDetails;